import { modalTypes } from "constants/types";
import { FILE_SOURCES } from "layout/documentUploader/constants";
import { compact, map, pick } from "lodash";
import { hideModal, showModal } from "slices/app/actions/modals";
import { addToJobQueue, removeFromJobQueue } from "slices/app/appSlice";
import { uploadFiles } from "slices/workspace/actions/uploadFilesHelpers";
import {
  cleanupFiles,
  updateFileUploadState,
} from "slices/workspace/foldersSlice";

import { UPLOAD_STATUSES } from "../../UploadFilesModal/constants";
import { CHUNK_SIZE, renameFile } from "../utils";

const JOB_KEY = "loadingFiles";
const DOCX_SUFFIX = /\.docx$/i;

const getBaseFileFields = (file) => ({
  name: file.content.name,
  size: file.content.size,
  hash: file.hash,
  ragDocumentId: file.id,
});

export const FILE_PAYLOAD_BUILDERS = {
  [FILE_SOURCES.LOCAL]: (file) => ({
    ...getBaseFileFields(file),
    totalChunks: Math.ceil(file.content.size / CHUNK_SIZE),
    source: "LOCAL",
  }),

  [FILE_SOURCES.SHAREPOINT]: (file) => ({
    ...getBaseFileFields(file),
    source: "SHAREPOINT",
    sharepointFilePathFromSite: `${file.content.filePath ?? file.content.path}/${file.content.name}`,
    sharepointSitePath: file.content.sitePath,
    sharepointHost: file.content.host,
  }),
};

const buildFilePayload = (file) => {
  const builder = FILE_PAYLOAD_BUILDERS[file.source];
  return builder ? builder(file) : null;
};

export const buildBasePayload = (files = [], baseInfo = {}) => ({
  ...pick(baseInfo, ["id", "name"]),
  files: compact(map(files, buildFilePayload)),
});

export const getTemplateName = (files) => {
  const [firstFile] = files;
  const fileName = firstFile?.content?.name ?? "";
  return fileName.replace(DOCX_SUFFIX, "");
};

// The template endpoint returns the created FileUpload rows directly, matched
// back to local files by name.
export const mapTemplateUploadIds = (localFiles, responseFiles = []) =>
  localFiles.reduce((acc, file) => {
    if (file.source !== FILE_SOURCES.LOCAL) {
      return acc;
    }
    const match = responseFiles.find((rf) => rf.fileName === file.content.name);
    if (match) {
      acc.push({ ...file, fileUploadId: match.id });
    }
    return acc;
  }, []);

// SharePoint templates are fetched and persisted server-side during /initialize, so they
// are displayed as already-complete and never chunk-uploaded from the browser.
export const resolveTemplateFiles = ({ data, files }) => {
  const filesToUpload = mapTemplateUploadIds(files, data);
  const sharepointFiles = files.filter(
    (file) => file.source === FILE_SOURCES.SHAREPOINT
  );

  return {
    filesToUpload,
    displayedFiles: [...filesToUpload, ...sharepointFiles],
  };
};

// Splits the flow initialize response into locally-uploadable files (chunked
// from the browser) and already-present SharePoint files (shown but not chunked).
export const resolveFlowFiles = ({
  data,
  responseFiles,
  files,
  isCollectionsTarget,
}) => {
  const uploadableNames = new Set(data?.filesToBeUploadedFromLocal ?? []);

  const matchResponseFile = isCollectionsTarget
    ? (rf, file) =>
        rf.hash === file.hash && rf.content.name === file.content.name
    : (rf, file) => rf.hash === file.hash;

  const getResponseFileName = (rf) => rf.content.name;

  const mapUploadIds = isCollectionsTarget
    ? (rf) => ({ ragDocumentId: rf.id, fileUploadId: rf.fileUploadId })
    : (rf) => ({ fileUploadId: rf.id });

  let remainingResponseFiles = [...responseFiles];
  const filesToUpload = [];

  for (const file of files) {
    if (file.source !== FILE_SOURCES.LOCAL) continue;
    if (uploadableNames.size === 0) continue;

    const responseFile = remainingResponseFiles.find((rf) =>
      matchResponseFile(rf, file)
    );
    if (!responseFile) continue;

    remainingResponseFiles = remainingResponseFiles.filter(
      (rf) => rf !== responseFile
    );

    let updatedFile = { ...file, ...mapUploadIds(responseFile) };

    const responseFileName = getResponseFileName(responseFile);
    if (updatedFile.content.name !== responseFileName) {
      updatedFile = {
        ...updatedFile,
        content: renameFile(updatedFile.content, responseFileName),
      };
    }

    if (uploadableNames.has(updatedFile.content.name)) {
      filesToUpload.push(updatedFile);
    }
  }

  const filesToProcess = remainingResponseFiles.filter((rf) =>
    data.filesToBeProcessed.includes(getResponseFileName(rf))
  );

  const sharepointFiles = files.filter((file) =>
    filesToProcess.some((fileToProcess) => fileToProcess.hash === file.hash)
  );

  const displayedFiles = [...filesToUpload, ...sharepointFiles];

  return { filesToUpload, displayedFiles };
};

export const createLoadingController = ({ dispatch, selectedCollection }) => {
  const usesModal = Boolean(selectedCollection?.id);

  return {
    start: () => {
      if (usesModal) {
        dispatch(
          showModal(modalTypes.LOADING_MODAL, {
            message: "Checking Files & Permissions",
          })
        );
      } else {
        dispatch(addToJobQueue(JOB_KEY));
      }
    },
    stop: () => {
      if (usesModal) {
        dispatch(hideModal(modalTypes.LOADING_MODAL));
      } else {
        dispatch(removeFromJobQueue(JOB_KEY));
      }
    },
  };
};

export const finalizeUpload = async ({
  dispatch,
  stopLoading,
  target,
  onFilesUploaded,
  metadata,
  filesToUpload,
  displayedFiles,
}) => {
  dispatch(cleanupFiles(displayedFiles));
  stopLoading();

  if (displayedFiles.length > 0) {
    dispatch(
      showModal(modalTypes.UPLOAD_FILES, {
        target,
        onFilesUploaded,
        displayedFiles,
      })
    );
  }

  if (filesToUpload.length) {
    await uploadFiles(filesToUpload, metadata, target);
  }
};

// No SSE stream backs the direct (template) upload, so completion is marked once
// the chunks have finished uploading rather than driven by stream events.
export const markUploadsComplete = ({ dispatch, files }) => {
  files.forEach((file) => {
    dispatch(
      updateFileUploadState({
        hash: file.hash,
        status: UPLOAD_STATUSES.COMPLETED,
        id: file.fileUploadId,
      })
    );
  });
};
